import { useState } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Upload, Download, Database, AlertTriangle, CheckCircle2, XCircle, FileJson, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DataImportModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Fetch database stats when modal opens
  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axiosInstance.get('/admin/database-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.json')) {
        toast.error('Apenas arquivos JSON são aceitos');
        return;
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo JSON');
      return;
    }

    if (replaceExisting) {
      const confirmed = window.confirm(
        '⚠️ ATENÇÃO: Você selecionou "Substituir dados existentes".\n\n' +
        'Isso irá APAGAR todos os dados atuais e substituir pelos dados do arquivo.\n\n' +
        'Tem certeza que deseja continuar?'
      );
      if (!confirmed) return;
    }

    setLoading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post(
        `/admin/import-data?replace_existing=${replaceExisting}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStats(); // Refresh stats
        if (onSuccess) onSuccess();
      } else {
        toast.warning('Importação concluída com alguns erros');
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao importar dados';
      toast.error(errorMsg);
      setImportResult({
        success: false,
        message: errorMsg,
        collections_imported: {},
        errors: [errorMsg]
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const response = await axiosInstance.get('/admin/export-data?collections=patients,prescriptions,health_units,vaccines,vaccination_records,delivery_dates,print_history');
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Dados exportados com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  // Handle load seed data
  const handleLoadSeedData = async (force = false) => {
    if (force) {
      const confirmed = window.confirm(
        '⚠️ ATENÇÃO: Isso irá APAGAR todos os dados atuais e carregar os dados de seed.\n\n' +
        'Tem certeza que deseja continuar?'
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`/admin/load-seed-data?force=${force}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStats();
        if (onSuccess) onSuccess();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Erro ao carregar seed data:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao carregar dados de seed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // When modal opens, fetch stats
  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      fetchStats();
      setFile(null);
      setImportResult(null);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Gerenciamento de Dados
          </DialogTitle>
          <DialogDescription>
            Importe ou exporte dados do banco de dados
          </DialogDescription>
        </DialogHeader>

        {/* Database Stats */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Database className="w-4 h-4" />
            Estatísticas Atuais do Banco
          </h3>
          
          {loadingStats ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando...
            </div>
          ) : stats ? (
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(stats.collections || {}).map(([name, count]) => (
                <div key={name} className="flex justify-between items-center p-2 bg-white rounded border">
                  <span className="text-sm text-gray-600 capitalize">{name.replace('_', ' ')}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">Não foi possível carregar estatísticas</p>
          )}
        </div>

        {/* Import Section */}
        <div className="p-4 border rounded-lg space-y-4">
          <h3 className="font-semibold text-gray-700 flex items-center gap-2">
            <Upload className="w-4 h-4 text-blue-500" />
            Importar Dados
          </h3>

          {/* File Input */}
          <div className="space-y-2">
            <Label htmlFor="file-upload" className="text-sm font-medium">
              Selecione o arquivo JSON
            </Label>
            <div className="flex items-center gap-3">
              <label 
                htmlFor="file-upload"
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
              >
                <FileJson className="w-4 h-4" />
                Escolher arquivo
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
              />
              {file && (
                <span className="text-sm text-gray-600">
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </span>
              )}
            </div>
          </div>

          {/* Replace Option */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Checkbox
              id="replace-existing"
              checked={replaceExisting}
              onCheckedChange={setReplaceExisting}
              className="mt-0.5"
            />
            <div>
              <Label htmlFor="replace-existing" className="text-sm font-medium text-amber-800 cursor-pointer">
                Substituir dados existentes
              </Label>
              <p className="text-xs text-amber-600 mt-1">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                CUIDADO: Isso irá apagar todos os dados atuais e substituir pelos do arquivo
              </p>
            </div>
          </div>

          {/* Import Button */}
          <Button
            onClick={handleImport}
            disabled={!file || loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Importar Dados
              </>
            )}
          </Button>
        </div>

        {/* Import Result */}
        {importResult && (
          <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-2">
              {importResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {importResult.message}
                </p>
                
                {Object.keys(importResult.collections_imported || {}).length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-gray-600">Registros importados:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(importResult.collections_imported).map(([name, count]) => (
                        <Badge key={name} variant="outline" className="bg-white">
                          {name}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600">Erros:</p>
                    <ul className="text-xs text-red-500 list-disc list-inside">
                      {importResult.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Export Section */}
        <div className="p-4 border rounded-lg">
          <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
            <Download className="w-4 h-4 text-green-500" />
            Exportar Dados
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Faça backup dos dados atuais em formato JSON
          </p>
          <Button
            onClick={handleExport}
            variant="outline"
            className="w-full border-green-200 text-green-700 hover:bg-green-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Backup (JSON)
          </Button>
        </div>

        {/* Seed Data Section */}
        <div className="p-4 border-2 border-dashed border-orange-200 rounded-lg bg-orange-50/50">
          <h3 className="font-semibold text-orange-700 flex items-center gap-2 mb-3">
            <Database className="w-4 h-4 text-orange-500" />
            Dados Pré-configurados (Seed Data)
          </h3>
          <p className="text-sm text-orange-600 mb-3">
            Carrega automaticamente os dados pré-configurados do sistema (pacientes, prescrições, unidades, etc.)
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => handleLoadSeedData(false)}
              disabled={loading}
              variant="outline"
              className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Carregar (se vazio)
            </Button>
            <Button
              onClick={() => handleLoadSeedData(true)}
              disabled={loading}
              variant="outline"
              className="flex-1 border-red-300 text-red-700 hover:bg-red-100"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Forçar Carga
            </Button>
          </div>
          <p className="text-xs text-orange-500 mt-2">
            ⚠️ "Forçar Carga" irá substituir todos os dados existentes
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
